import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppService, ClientCustomizationService, UserStateService } from 'src/app/services/api.services';
import { AppContext } from 'src/app/app/context';
import { AuthService } from '../../auth/auth.service';
import { catchAppError } from '../../shared/utils';
import * as AppActions from './app.actions';
import * as AppSelectors from './app.selectors';

@Injectable()
export class AppEffects {
  constructor(
    private readonly store: Store,
    private readonly appService: AppService,
    private readonly userStateService: UserStateService,
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly clientCustomizationService: ClientCustomizationService,
    private readonly appContext: AppContext) { }

  loadSettings$ = createEffect(() => this.actions$.pipe(
    ofType(AppActions.loadSettings),
    switchMap(() => this.appService.getSettings().pipe(
      map(settings => {
        this.appContext.settings.set(settings);
        return AppActions.loadSettingsSuccess(settings);
      })
    ))
  ));

  loadClientCustomization$ = createEffect(() => this.actions$.pipe(
    ofType(AppActions.loadClientCustomization),
    switchMap(() => this.clientCustomizationService.getUserClientCustomization().pipe(
      map(clientCustomization => AppActions.loadClientCustomizationSuccess(clientCustomization))
    ))
  ));


  logout$ = createEffect(() => this.actions$.pipe(
    ofType(AppActions.logout),
    tap(() => {
      this.authService.logout(true);
    })
  ), { dispatch: false });

  saveUserStateAfterUpdate$ = createEffect(() => this.actions$.pipe(
    ofType(AppActions.updateUserState),
    withLatestFrom(this.store.select(AppSelectors.getUserState)),
    map(([{ userState, callbackAction }, currentUserState]) => AppActions.saveUserState({
      userState: {
        ...currentUserState,
        ...userState,
      },
      callbackAction
    }))
  ));

  saveUserState$ = createEffect(() => this.actions$.pipe(
    ofType(AppActions.saveUserState),
    switchMap(({ userState, callbackAction }) => this.userStateService.setUserState(userState).pipe(
      map(() => AppActions.saveUserStateSuccess({ userState, callbackAction })),
      catchAppError(error => of(AppActions.saveUserStateFailure({ error })), { errorMessage: 'Failed to load user state. Some application defaults may be reverted.' })
    ))
  ));

  callbackAfterSaveUserStateSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AppActions.saveUserStateSuccess),
    filter(({ callbackAction }) => !!callbackAction),
    map(({ callbackAction }) => callbackAction)
  ));
}
